/**
 * @fileoverview O editor de um agente de IA, nas abas Conhecimento (o texto,
 * com as versões), Fontes (site, arquivos, respostas prontas, pesquisa e
 * catálogo da loja), Inteligência (modelo, raciocínio, jeito de conversar,
 * mídia, espera, quem aparece e a foto) e Testar. "Salvar" grava o agente e,
 * se o texto mudou, uma versão nova dele, que entra no ar na hora (vale em
 * todos os fluxos que usam o agente). Sem ai_agents.manage, só mostra (e sem a
 * aba Testar, que o servidor recusaria).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import { ChevronLeft, X } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";
import { i18n } from "../../../../translate/i18n";
import useStyles from "../TriageManager/styles";
import useAgentStyles from "./styles";
import AgentAvatar from "./AgentAvatar";
import KnowledgeTab from "./KnowledgeTab";
import SourcesTab from "./SourcesTab";
import IntelligenceTab from "./IntelligenceTab";
import TestTab from "./TestTab";
import { usedInText } from "./AgentList";

const TABS = ["knowledge", "sources", "intelligence", "test"];
// A espera pelo cliente terminar de digitar: os limites do bloco do fluxo (sem ela, o fluxo espera 12 s).
const WAIT_MIN = 3;
const WAIT_MAX = 60;
const WAIT_DEFAULT = 12;

const EMPTY_FORM = {
  name: "",
  prompt: "",
  model: "gpt-5-mini",
  reasoningEffort: "medium",
  responseStyle: "concise",
  splitMessages: true,
  processImages: false,
  processAudio: false,
  processVideo: false,
  responseDelay: 12,
  aiUserId: "",
  sourceUrl: "",
  knowledgeFiles: [],
  cases: [],
  webSearchEnabled: false,
  webSearchDomains: "",
  wooIntegrationIds: [],
};

export const activeVersionOf = (agent) => (agent?.versions || []).find((version) => version.id === agent.activeVersionId) || null;

const formOf = (agent) => ({
  name: agent.name || "",
  prompt: activeVersionOf(agent)?.optimizedPrompt || "",
  model: agent.model || "gpt-5-mini",
  reasoningEffort: agent.reasoningEffort || "medium",
  responseStyle: agent.responseStyle || "concise",
  splitMessages: agent.splitMessages !== false,
  processImages: agent.processImages === true,
  processAudio: agent.processAudio === true,
  processVideo: agent.processVideo === true,
  responseDelay: agent.responseDelay || WAIT_DEFAULT,
  aiUserId: agent.aiUserId || "",
  sourceUrl: agent.sourceUrl || "",
  knowledgeFiles: Array.isArray(agent.knowledgeFiles) ? agent.knowledgeFiles : [],
  cases: Array.isArray(agent.cases) ? agent.cases : [],
  webSearchEnabled: agent.webSearchEnabled === true,
  webSearchDomains: agent.webSearchDomains || "",
  wooIntegrationIds: Array.isArray(agent.wooIntegrationIds) ? agent.wooIntegrationIds : [],
});

// O que vai para o agente (o texto vai como versão).
const payloadOf = (form) => ({
  name: form.name.trim(),
  model: form.model,
  reasoningEffort: form.reasoningEffort,
  responseStyle: form.responseStyle,
  splitMessages: form.splitMessages,
  processImages: form.processImages,
  processAudio: form.processAudio,
  processVideo: form.processVideo,
  responseDelay: Math.min(WAIT_MAX, Math.max(WAIT_MIN, Number(form.responseDelay) || WAIT_DEFAULT)),
  aiUserId: form.aiUserId || null,
  sourceUrl: form.sourceUrl.trim() || null,
  knowledgeFiles: form.knowledgeFiles,
  // A resposta pronta vale pela resposta (a pergunta pode faltar, como no servidor); o cartão em branco sai.
  cases: form.cases.map((item) => ({ ...item, name: item.name.trim(), content: item.content.trim() })).filter((item) => item.content),
  webSearchEnabled: form.webSearchEnabled,
  webSearchDomains: form.webSearchDomains,
  wooIntegrationIds: form.wooIntegrationIds,
});

const AgentEditor = ({ agentId, canManage, onBack, onClose, onDirtyChange, onCreated }) => {
  const classes = useStyles();
  const agentClasses = useAgentStyles();
  const [tab, setTab] = useState("knowledge");
  const [agent, setAgent] = useState(null);
  const [form, setForm] = useState(agentId ? null : EMPTY_FORM);
  const [saved, setSaved] = useState(agentId ? null : EMPTY_FORM);
  const [loading, setLoading] = useState(!!agentId);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({ aiUsers: [], stores: [] });

  const load = useCallback(async (id) => {
    const { data } = await api.get(`/ai-agents/${id}`);
    setAgent(data);
    setForm(formOf(data));
    setSaved(formOf(data));
    return data;
  }, []);

  useEffect(() => {
    if (!agentId) return;
    setLoading(true);
    load(agentId)
      .catch((err) => {
        setLoadFailed(true);
        toastError(err);
      })
      .finally(() => setLoading(false));
  }, [agentId, load]);

  // Os usuários IA (quem aparece como atendente) e as lojas ativas do WooCommerce.
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/users/list", { params: { onlyAi: true } }).then(({ data }) => (Array.isArray(data) ? data : [])).catch(() => []),
      api.get("/ai-agents/stores").then(({ data }) => (Array.isArray(data) ? data : [])).catch(() => []),
    ]).then(([aiUsers, stores]) => {
      if (alive) setOptions({ aiUsers, stores });
    });
    return () => {
      alive = false;
    };
  }, []);

  const dirty = useMemo(() => !!form && !!saved && JSON.stringify(form) !== JSON.stringify(saved), [form, saved]);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const set = (changes) => setForm((current) => ({ ...current, ...changes }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error(i18n.t("agentManager.editor.nameRequired"));
      return;
    }
    // Não há como tirar o texto do ar: apagar e salvar faria o antigo voltar sem aviso.
    if (!form.prompt.trim() && saved.prompt.trim()) {
      toast.error(i18n.t("agentManager.editor.promptRequired"));
      return;
    }
    if (form.cases.some((item) => item.name.trim() && !item.content.trim())) {
      toast.error(i18n.t("agentManager.editor.caseNeedsAnswer"));
      return;
    }
    setSaving(true);
    try {
      let id = agent?.id;
      if (id) {
        await api.put(`/ai-agents/${id}`, payloadOf(form));
      } else {
        const { data } = await api.post("/ai-agents", payloadOf(form));
        id = data.id;
      }
      // O texto novo vira uma versão (e entra no ar).
      const prompt = form.prompt.trim();
      if (prompt && prompt !== saved.prompt.trim()) {
        await api.post(`/ai-agents/${id}/versions`, { originalDescription: prompt.slice(0, 4000), optimizedPrompt: prompt });
      }
      await load(id);
      toast.success(i18n.t(agent ? "agentManager.editor.saved" : "agentManager.editor.created"));
      if (!agent) onCreated(id);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // A foto já foi gravada: só ela muda aqui (o que está em edição fica).
  const photoSaved = (profilePicUrl) => setAgent((current) => ({ ...current, profilePicUrl }));

  const reload = async () => {
    try {
      await load(agent.id);
    } catch (err) {
      toastError(err);
    }
  };

  const title = form?.name?.trim() || (agentId ? "…" : i18n.t("agentManager.editor.newTitle"));
  const readOnly = !canManage;
  const tabs = canManage ? TABS : TABS.filter((key) => key !== "test");

  return (
    <>
      <div className={classes.header}>
        <button type="button" className={classes.iconBtn} onClick={onBack} disabled={saving} title={i18n.t("agentManager.editor.back")} aria-label={i18n.t("agentManager.editor.back")}>
          <ChevronLeft />
        </button>
        <div className={agentClasses.headerAgent}>
          <AgentAvatar name={title} profilePicUrl={agent?.profilePicUrl} size="small" />
          <div style={{ minWidth: 0 }}>
            <div className={`${classes.title} ${classes.titleSmall}`}>{title}</div>
            {agent && <div className={agentClasses.usedIn}>{usedInText(agent.usedIn)}</div>}
          </div>
        </div>
        <button type="button" className={classes.iconBtn} onClick={onClose} disabled={saving} aria-label={i18n.t("agentManager.close")}>
          <X />
        </button>
      </div>

      <div className={classes.tabs}>
        {tabs.map((key) => (
          <button key={key} type="button" className={`${classes.tab} ${tab === key ? classes.tabActive : ""}`} onClick={() => setTab(key)}>
            {i18n.t(`agentManager.editor.tabs.${key}`)}
          </button>
        ))}
      </div>

      <div className={classes.body}>
        {loadFailed ? (
          <div className={classes.empty}>{i18n.t("agentManager.editor.loadFailed")}</div>
        ) : loading || !form ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            {tab === "knowledge" && (
              <div className={classes.form} style={{ maxWidth: "none" }}>
                <TextField
                  className={classes.field}
                  variant="outlined"
                  size="small"
                  label={i18n.t("agentManager.editor.name")}
                  placeholder={i18n.t("agentManager.editor.namePlaceholder")}
                  value={form.name}
                  onChange={(event) => set({ name: event.target.value })}
                  inputProps={{ maxLength: 60 }}
                  InputLabelProps={{ shrink: true }}
                  disabled={readOnly}
                  autoFocus={!agentId}
                  style={{ maxWidth: 360 }}
                />
                <KnowledgeTab agent={agent} form={form} onChange={set} readOnly={readOnly} dirty={dirty} onVersionUsed={reload} />
              </div>
            )}
            {tab === "sources" && <SourcesTab form={form} onChange={set} readOnly={readOnly} stores={options.stores} />}
            {tab === "intelligence" && <IntelligenceTab agent={agent} form={form} onChange={set} readOnly={readOnly} aiUsers={options.aiUsers} onPhotoSaved={photoSaved} />}
            {tab === "test" && canManage && <TestTab agent={agent} dirty={dirty} />}
          </>
        )}
      </div>

      <div className={classes.footer}>
        <div className={classes.footerInfo}>
          {readOnly ? i18n.t("agentManager.editor.readOnly") : dirty ? <span className={classes.footerWarn}>{i18n.t("agentManager.editor.unsavedHint")}</span> : null}
        </div>
        {!readOnly && (
          <div className={classes.footerButtons}>
            <button type="button" className={classes.btnGhost} onClick={() => setForm(saved)} disabled={!form || !dirty || saving}>
              {i18n.t("agentManager.editor.discard")}
            </button>
            <button type="button" className={classes.btnPrimary} onClick={save} disabled={saving || !form || (!dirty && !!agent)}>
              {saving && <CircularProgress size={14} style={{ color: "inherit" }} />}
              {i18n.t("agentManager.editor.save")}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default AgentEditor;
